import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Store, Plus, Loader2, Trash2, ImagePlus, Check, ExternalLink } from "lucide-react";

export default function MerchantShopManager() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price_blc: "", stock: "1" });
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);

  const load = () => { api.get("/shop/mine").then(({ data: d }) => setData(d)).catch(() => {}); };
  useEffect(load, []);

  if (!data || !data.merchant) return null;

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(f);
  };

  const addProduct = async () => {
    setBusy("add");
    try {
      await api.post("/shop/products", { ...form, price_blc: Number(form.price_blc) || 0, stock: Number(form.stock) || 0, image });
      toast.success(`"${form.name}" is live in your shop`);
      setForm({ name: "", description: "", price_blc: "", stock: "1" });
      setImage(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/shop/products/${p.id}`, { name: p.name, description: p.description, price_blc: p.price_blc, stock: p.stock, active: !p.active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    try { await api.delete(`/shop/products/${p.id}`); toast.success(`"${p.name}" removed`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fulfill = async (o) => {
    setBusy(o.id);
    try { await api.post(`/shop/orders/${o.id}/fulfill`); toast.success("Order marked fulfilled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-pb-surface border border-pb-cyan/40 p-6 mb-8" data-testid="merchant-shop-manager">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-pb-cyan" />
          <span className="font-display font-bold text-lg uppercase">My Storefront — {data.merchant.business_name}</span>
        </div>
        <a href={`/shops/${data.merchant.id}`} target="_blank" rel="noreferrer" data-testid="shop-public-link"
          className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-pb-cyan hover:underline">
          <ExternalLink className="w-3 h-3" /> View public shop
        </a>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        List goods for players to buy with BULLION COIN. Sales land in your earned balance (5% platform fee) —
        redeem them for settlement payouts from the Redemption Portal.
      </p>

      <div className="border border-pb-border bg-pb-elevated/40 p-4 mb-5 space-y-2.5">
        <div className="flex flex-wrap gap-2.5">
          <input data-testid="shop-product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Product name *" className="flex-1 min-w-[160px] bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-cyan" />
          <input data-testid="shop-product-price" type="number" min={0.5} step={0.5} value={form.price_blc}
            onChange={(e) => setForm({ ...form, price_blc: e.target.value })} placeholder="Price (BLC) *"
            className="w-28 bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-pb-cyan" />
          <input data-testid="shop-product-stock" type="number" min={0} step={1} value={form.stock}
            onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock"
            className="w-20 bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-pb-cyan" />
        </div>
        <textarea data-testid="shop-product-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Short description" rows={2}
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-cyan" />
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="shop-product-image-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="shop-product-image-btn"
            className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2.5 hover:border-zinc-400 transition-colors">
            <ImagePlus className="w-3.5 h-3.5" /> {image ? "Photo added ✓" : "Add photo"}
          </button>
          {image && <img src={image} alt="" className="w-10 h-10 object-cover border border-pb-border" />}
          <button data-testid="shop-product-add" onClick={addProduct} disabled={busy === "add" || !form.name.trim() || !Number(form.price_blc)}
            className="flex items-center gap-2 bg-pb-cyan text-black font-black uppercase tracking-widest px-5 py-2.5 text-[10px] hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
            {busy === "add" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} List Product
          </button>
        </div>
      </div>

      {data.products.length > 0 && (
        <div className="divide-y divide-pb-border/40 border border-pb-border mb-5" data-testid="shop-my-products">
          {data.products.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid={`shop-my-product-${p.id}`}>
              {p.image ? <img src={p.image} alt="" className="w-9 h-9 object-cover border border-pb-border" /> : <div className="w-9 h-9 bg-pb-elevated border border-pb-border" />}
              <div className="flex-1 min-w-[120px]">
                <div className="text-xs font-semibold">{p.name}</div>
                <div className="text-[9px] text-zinc-600">{p.price_blc} BLC · {p.stock} in stock · {p.sold} sold</div>
              </div>
              <button data-testid={`shop-toggle-${p.id}`} onClick={() => toggleActive(p)}
                className={`text-[8px] font-black uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${p.active ? "border-emerald-500/60 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>
                {p.active ? "Live" : "Hidden"}
              </button>
              <button data-testid={`shop-delete-${p.id}`} onClick={() => remove(p)} className="p-1.5 text-zinc-500 hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {data.orders.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Orders</div>
          <div className="divide-y divide-pb-border/40 border border-pb-border max-h-56 overflow-y-auto" data-testid="shop-orders">
            {data.orders.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs" data-testid={`shop-order-${o.id}`}>
                <span className="text-zinc-300">{o.buyer_name} — {o.product_name} <span className="text-lime-300 font-mono">+{o.merchant_take} BLC</span></span>
                {o.status === "paid" ? (
                  <button data-testid={`shop-fulfill-${o.id}`} onClick={() => fulfill(o)} disabled={!!busy}
                    className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-black px-3 py-1.5 hover:bg-[#BEF264] transition-colors disabled:opacity-50">
                    {busy === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Fulfill
                  </button>
                ) : (
                  <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400">Fulfilled</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
